import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Heading } from "@/components/ui/heading";
import { Text } from "@/components/ui/text";
import Container from "../../components/Container";
import Page from "../../components/Page";
import Spinner from "../../components/ui/spinner";
import { adminEmployeesApi } from "../api/employees";
import { adminEmployeeCsatScoresApi } from "../api/employeeCsatScores";
import { adminEmployeePointsCalculationsApi } from "../api/employeePointsCalculations";
import { ApiError } from "../api/utils";
import type {
  AdminEmployee,
  AdminEmployeeCsatScore,
  AdminEmployeePointsCalculation,
} from "../modules/types";

type PageData = {
  employee: AdminEmployee;
  csatScores: AdminEmployeeCsatScore[];
  pointsCalculations: AdminEmployeePointsCalculation[];
};

const AdminEmployeePage: React.FunctionComponent = () => {
  const { workspaceId, employeeId } = useParams<{
    workspaceId: string;
    employeeId: string;
  }>();
  const [data, setData] = useState<PageData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!employeeId || !workspaceId) return;
    let isMounted = true;

    // Main's admin API only exposes workspace-scoped filters for csat scores
    // and points calculations, so we fetch the workspace-wide list and narrow
    // to this employee on the client.
    Promise.all([
      adminEmployeesApi.getById(employeeId),
      adminEmployeeCsatScoresApi.getAll(workspaceId),
      adminEmployeePointsCalculationsApi.getAll(workspaceId),
    ])
      .then(([employee, allCsatScores, allPointsCalculations]) => {
        if (!isMounted) return;
        setData({
          employee,
          csatScores: allCsatScores
            .filter((score) => score.employeeId === employeeId)
            .sort((a, b) => b.calculatedOn.localeCompare(a.calculatedOn)),
          pointsCalculations: allPointsCalculations
            .filter((calc) => calc.employeeId === employeeId)
            .sort((a, b) => b.calculatedOn.localeCompare(a.calculatedOn)),
        });
      })
      .catch((err: unknown) => {
        if (!isMounted) return;
        setError(
          err instanceof ApiError ? err.message : "Failed to load employee",
        );
      });

    return () => {
      isMounted = false;
    };
  }, [workspaceId, employeeId]);

  if (error) {
    return (
      <Page>
        <Container>
          <div className="bg-destructive/10 border border-destructive/40 px-4 py-3 rounded">
            <Text variant="sm" className="text-destructive">{error}</Text>
          </div>
        </Container>
      </Page>
    );
  }

  if (!data) {
    return (
      <Page>
        <Container>
          <div className="flex justify-center py-12">
            <Spinner size={32} className="text-muted-foreground" />
          </div>
        </Container>
      </Page>
    );
  }

  const { employee, csatScores, pointsCalculations } = data;

  return (
    <Page>
      <Container>
        <Link
          to={workspaceId ? `/admin/workspaces/${workspaceId}` : "/admin/workspaces"}
          className="text-muted-foreground text-sm hover:underline mb-2 inline-block"
        >
          &larr; Back to workspace
        </Link>
        <Heading level={1} variant="lg">{employee.name}</Heading>
        <Text variant="xl" className="text-muted-foreground mb-6">
          {employee.email}
        </Text>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Info</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2">
                <Text variant="sm" className="font-semibold">Scoring</Text>
                <Text variant="sm">{employee.isScoring ? "Yes" : "No"}</Text>
                <Text variant="sm" className="font-semibold">Notify manager</Text>
                <Text variant="sm">{employee.notifyManager ? "Yes" : "No"}</Text>
                <Text variant="sm" className="font-semibold">Manager ID</Text>
                <Text variant="sm">{employee.managerId || "\u2014"}</Text>
                <Text variant="sm" className="font-semibold">External agent ID</Text>
                <Text variant="sm">{employee.externalAgentId || "\u2014"}</Text>
                <Text variant="sm" className="font-semibold">External messaging ID</Text>
                <Text variant="sm">{employee.externalMessagingId || "\u2014"}</Text>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>CSAT Scores ({csatScores.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {csatScores.length === 0 ? (
                <Text variant="sm" className="text-muted-foreground">
                  No CSAT scores calculated yet.
                </Text>
              ) : (
                <div className="grid grid-cols-[auto_1fr_auto] gap-x-6 gap-y-2">
                  <Text variant="sm" className="font-semibold">Calculated on</Text>
                  <Text variant="sm" className="font-semibold">Period</Text>
                  <Text variant="sm" className="font-semibold">Score</Text>
                  {csatScores.map((score) => (
                    <React.Fragment key={score.id}>
                      <Text variant="sm">{score.calculatedOn}</Text>
                      <Text variant="sm" className="text-muted-foreground">
                        {score.periodStartsOn} &ndash; {score.periodEndsOn}
                      </Text>
                      <Text variant="sm">{score.score}</Text>
                    </React.Fragment>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Points Calculations ({pointsCalculations.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {pointsCalculations.length === 0 ? (
                <Text variant="sm" className="text-muted-foreground">
                  No points calculations yet.
                </Text>
              ) : (
                <div className="space-y-3">
                  {pointsCalculations.map((calc) => (
                    <div key={calc.id} className="border rounded-md p-3">
                      <div className="flex justify-between items-baseline mb-2">
                        <Text variant="sm" className="font-semibold">
                          {calc.calculatedOn}
                        </Text>
                        <Text variant="sm">
                          {calc.totalPoints} points
                        </Text>
                      </div>
                      {calc.pointsByAction && Object.keys(calc.pointsByAction).length > 0 && (
                        <div className="grid grid-cols-[1fr_auto_auto] gap-x-6 gap-y-1">
                          {Object.entries(calc.pointsByAction).map(([action, details]) => (
                            <React.Fragment key={action}>
                              <Text variant="sm" className="text-muted-foreground">{action}</Text>
                              <Text variant="sm" className="text-muted-foreground">
                                {details.count}x
                              </Text>
                              <Text variant="sm" className="text-muted-foreground">
                                {details.points} pts
                              </Text>
                            </React.Fragment>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </Container>
    </Page>
  );
};

export default AdminEmployeePage;
